import { useMutation } from "@tanstack/react-query";
import { ButtonWarning } from "components/Atoms/Button";
import { OptionalDatePicker } from "components/DatePicker/OptionalDatePicker";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { emptySubmitChangeFunc } from "components/MainSectionTemplate/emptySubmitChangeFunc";
import { NoDataAvailableDiv } from "components/NoDataAvailableDiv";
import { Loading } from "components/panels";
import PanelSegment from "components/panels/PanelSegment/PanelSegment";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import { BookkeepingMultiModalPicker, SelectedBookkeepings } from "components/Picker/Bookkeeping/BookkeepingMultiModalPicker";
import StatusMultiModalPicker, { SelectedStatuses } from "components/Picker/Status/StatusMultiModalPicker";
import TwoColumnLayout from "layout/TwoColumnLayout";
import moment from "moment";
import React from "react";
import { Message } from "components/Atoms/Message";
import { useCustomQuery } from "utilities/hookutils";
import { formatShortDate } from "utilities";

type ResultState = "noDateSelected" | "loading" | "noData" | "dataAvailable";

export const LockShowsApp = () => {
  const [statuses, setStatuses] = React.useState<server.dto.StatusSmall[]>([]);
  const [bookkeepings, setBookkeepings] = React.useState<server.dto.BookkeepingSmall[]>([]);
  const [before, setBefore] = React.useState<moment.Moment>(null);

  const peekQuery = useCustomQuery({
    queryKey: ["peek", statuses, bookkeepings, before],
    queryFn: () => window.CoreApi.Show.CountUnlockedShows({
      statusIds: statuses.map(s => s.statusId),
      bookkeepingIds: bookkeepings.map(b => b.bookkeepingId),
      before: before.toDate(),
    }),
    enabled: before != null
  });

  const lockMutation = useMutation({
    mutationFn: window.CoreApi.Show.BatchLockShows,
  })

  const onLockClicked = async () => {
    if (lockMutation.isLoading) return;

    let errorMessage: string;
    let result: string;
    try {
      const res = await lockMutation.mutateAsync({
        statusIds: statuses.map(s => s.statusId),
        bookkeepingIds: bookkeepings.map(b => b.bookkeepingId),
        before: before.toDate(),
      });

      if (res.data.hasErrors) {
        errorMessage = res.data.errors[0].message;
      } else {
        result = res.data.data.message;
      }
    } catch {
      errorMessage = RESX.GeneralLabels.SomethingUnexpectedHappened;
    }

    if (errorMessage) {
      window.displayGenericModal(RESX.Show.LockShows, errorMessage, "mini", "error");
      return;
    } else {
      window.displayGenericModal(RESX.Show.LockShows, result, "mini", "info");
    }

    peekQuery.refetch();
  }

  const lockButtonDisabled = before == null || peekQuery.data?.data.data.count === 0 || lockMutation.isLoading || peekQuery.isLoading;

  let resultState: ResultState;
  if (before == null) {
    resultState = "noDateSelected";
  } else if (peekQuery.isLoading) {
    resultState = "loading";
  } else if (peekQuery.data?.data.data.count === 0) {
    resultState = "noData";
  } else {
    resultState = "dataAvailable";
  }

  const onValueChanged = (key: string, value: any) => {
    if (key === "status") {
      setStatuses(value);
    } else if (key === "bookkeepings") {
      setBookkeepings(value);
    } else if (key === "before") {
      setBefore(value);
    }
  }

  return <TwoColumnLayout>
    <TwoColumnLayout.LeftColumn>
      <DetailSectionTemplate
        isInPageEditable
        onValueChanged={onValueChanged}
        sections={[{
          key: "lockShows",
          type: "regular",
          title: RESX.Show.LockShows,
          fields: [{
            key: "status",
            label: RESX.Status.resxStatus,
            isEmpty: statuses.length === 0,
            render: () => <SelectedStatuses value={statuses} />,
            inPageEdit: {
              fullWidth: true,
              disableClickOutside: true,
              render: ({ value, onChange, cancel }) => <StatusMultiModalPicker
                inPageEditing={{ cancel }}
                mainArea="show"
                value={value}
                onChange={v => onChange(v, true)}
                canCreateNew={false}
                required={false}
                validatorKey="status"
              />,
              initialValue: statuses,
              type: "custom",
              submitChange: emptySubmitChangeFunc,
            }
          }, {
            key: "bookkeepings",
            label: RESX.Bookkeeping.resxBookkeeping,
            isEmpty: bookkeepings.length === 0,
            render: () => <SelectedBookkeepings value={bookkeepings} />,
            inPageEdit: {
              fullWidth: true,
              disableClickOutside: true,
              render: ({ value, onChange, cancel }) => <BookkeepingMultiModalPicker
                inPageEditing={{ cancel }}
                value={value}
                onChange={v => onChange(v, true)}
                canCreateNew={false}
                required={false}
                bookkeepingSecurityScope="limitByUserAccess"
                validatorKey="bookkeepings"
              />,
              initialValue: bookkeepings,
              type: "custom",
              submitChange: emptySubmitChangeFunc,
            }
          }, {
            key: "before",
            label: RESX.DateTime.Before,
            isEmpty: before == null,
            render: () => formatShortDate(before),
            inPageEdit: {
              render: ({ value, onChange }) => <OptionalDatePicker
                focusOnMount
                value={value}
                onChange={v => onChange(v)}
                minDate={moment.utc(serverReference.constants.appointment.minDate)}
                maxDate={moment.utc(serverReference.constants.appointment.maxDate)}
                validatorKey="before"
              />,
              initialValue: before,
              type: "confirmOrCancelButtonInline",
              submitChange: emptySubmitChangeFunc,
            }
          }]
        }]}
      />
    </TwoColumnLayout.LeftColumn>
    <TwoColumnLayout.RightColumn>
      <SectionPanel.Section>
        <SectionPanel.Panel
          header={<PanelSegment.Title title={"Selected shows"} />}
        >
          <div className="flex-column gap-large">

            {resultState === "noDateSelected" && <Message type="warning">
              {RESX.DateTime.PickADate}
            </Message>}
            {resultState === "loading" && <Loading />}
            {resultState === "noData" && <NoDataAvailableDiv />}
            {resultState === "dataAvailable" && <Message type="info">
              {peekQuery.data.data.data.message}
            </Message>}

            <div>
              <ButtonWarning
                label={RESX.InvoiceExport.Lock}
                onClick={onLockClicked}
                loading={lockMutation.isLoading}
                disabled={lockButtonDisabled}
              />
            </div>
          </div>
        </SectionPanel.Panel>
      </SectionPanel.Section>
    </TwoColumnLayout.RightColumn>

  </TwoColumnLayout>
}
